<script lang="ts">
  import type { Current } from '../api/types'
  import { relativeTime } from '../format'
  import { connection } from '../state/connection.svelte'
  import { hereNote, hereNoteText } from '../state/here.svelte'
  import { radarLine } from '../forecast/now'
  import { location, selectedBundle } from '../state/location.svelte'
  import { radarOverrideFor } from '../state/now.svelte'
  import { places } from '../state/places.svelte'

  interface Props {
    /**
     * The observation the forecast came with, for 1B's station line:
     * "Lee's Summit Regional Airport, 8:15 PM · Updated 3 min ago". The desktop
     * passes it; the phone's header has no room for it (Phase 10 plan §5.1).
     */
    current?: Current | null
    /**
     * Only what is not routine: locating, the last-known fix, an error, the
     * places message, offline. "Updated just now" and "Loading…" are not said
     * at all, and with nothing else to say the line is not there. For the
     * phone's collapsed header, where 1B has no station line (Phase 10 plan
     * §5.1); the refresh every five minutes is a load too, and would blink.
     */
    quiet?: boolean
  }

  let { current = null, quiet = false }: Props = $props()

  let station = $derived(current?.station_name ?? current?.station_id ?? '')
  // In the point's own zone, as the Next hour, hourly and week sections under
  // this line are: a station at 6:45 PM over a radar at 8:00 PM read as a
  // three-hour gap from a phone an hour east of the point (2026-11-03).
  let timeZone = $derived(selectedBundle()?.location.timezone ?? undefined)
  // The observation's own time, which can be an hour older than the fetch.
  let observed = $derived(
    current?.observed_at
      ? new Date(current.observed_at).toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
          ...(timeZone ? { timeZone } : {}),
        })
      : '',
  )

  // When the radar's words are in the hero, the line says so and when: the
  // station keeps its name and time, and the description is no longer its.
  let radarNote = $derived.by(() => {
    const radar = radarOverrideFor(selectedBundle())
    return radar ? radarLine(radar, timeZone) : ''
  })

  // "3 min ago" has to age on its own; a minute is as fine as the text gets.
  let now = $state(new Date())
  $effect(() => {
    const timer = setInterval(() => (now = new Date()), 60_000)
    return () => clearInterval(timer)
  })

  let age = $derived(relativeTime(location.fetchedAt, now))
  // Why the boot is showing what it shows: locating, the last fix, or the last
  // place viewed because location could not be had. Gone at the next choice.
  let note = $derived(hereNote())
  let noteText = $derived(note ? hereNoteText(note, now) : '')

  let offline = $derived(connection.status === 'offline' || connection.status === 'unreachable')
  let unusual = $derived(
    !!noteText || location.status === 'error' || !!places.message || offline,
  )
</script>

{#if !quiet || unusual}
  <p class="status" class:quiet aria-live="polite">
    {#if noteText}
      <span class="note">{noteText}</span>
    {/if}
    {#if location.status === 'loading'}
      {#if !quiet}<span class="muted">Loading…</span>{/if}
    {:else if location.status === 'error'}
      <span class="err">{location.error}</span>
    {:else if age && note?.kind !== 'locating' && !quiet}
      <!-- Never "Updated just now" alone over a point that is not yet known to
           be where the phone is. -->
      {#if station}<span class="muted station"
          >{station}{#if observed}, {observed}{/if}<span aria-hidden="true">{' ·'}</span></span
        >{/if}
      {#if radarNote}<span class="muted radar"
          >{radarNote}<span aria-hidden="true">{' ·'}</span></span
        >{/if}
      <span class="muted">Updated {age}</span>
    {/if}
    {#if places.message}
      <!-- "Saved Home", "20 places is the limit": about a thing just done, so
           it goes after a few seconds. -->
      <span class="note">{places.message}</span>
    {/if}
    {#if connection.status === 'offline'}
      <span class="offline">Offline — showing saved forecast</span>
    {:else if connection.status === 'unreachable'}
      <span class="offline">Server unreachable — showing saved forecast</span>
    {/if}
  </p>
{/if}

<style>
  /* 1B's station line: 11 px in the muted ink, under the hero. */
  .status {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 6px;
    align-items: center;
    font-size: 11px;
    line-height: 1.4;
    margin: 0;
    min-height: 1.4em;
  }

  /* In the phone's fixed-height header: two lines at most, then an ellipsis. */
  .status.quiet {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    min-height: 0;
  }

  .status.quiet > span {
    margin-right: 6px;
  }

  .err {
    color: var(--color-danger);
  }

  .note {
    color: var(--color-text);
  }

  .offline {
    background: var(--color-warn);
    color: var(--color-on-warn);
    padding: 1px 6px;
    font-weight: 800;
  }
</style>
